import React, { useState, useEffect } from "react";
import { api } from "../utils/api";
import { PageHeader, Card } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Button, SearchInput } from "../components/ui/Button";
import { TableCard, td, trStyle } from "../components/ui/Table";
import { LoadingRow } from "../components/ui/EmptyState";
import { PreviewModal } from "../components/ui/Modal";
import { colors } from "../components/ui/tokens";
import { StarIcon, CheckIcon, LoginAsIcon, PinIcon } from "../components/ui/icons";

// Réservé au compte master (voir OLTODO-backend/src/middleware/auth.ts#requireMasterAdmin) —
// pas de contexte partagé dans oltodo-admin, admin_user en localStorage fait référence.
const isMasterAdmin = () => {
  try { return !!JSON.parse(localStorage.getItem("admin_user") || "null")?.is_master_admin; } catch { return false; }
};

// Natures de pièce justificative (facultative) — voir VERIFICATION_DOC_TYPES dans OLTODO-backend/src/routes/users.ts.
const DOC_TYPE_LABELS: Record<string, string> = {
  id_card: "Carte d'identité", passport: "Passeport", driver_license: "Permis de conduire",
  utility_bill: "Facture", transit_pass: "Titre de transport", other: "Autre justificatif",
};

const initials = (name: string) => name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();

export default function Users() {
  const [users, setUsers] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [verificationRequests, setVerificationRequests] = useState<any[]>([]);

  const loadVerificationRequests = () => {
    api.get("/users/admin/verification-requests")
      .then(data => { if (data && Array.isArray(data.requests)) setVerificationRequests(data.requests); })
      .catch(() => {});
  };

  useEffect(() => {
    api.get("/users")
      .then(data => { if (data && Array.isArray(data.users)) setUsers(data.users); })
      .catch(() => {})
      .finally(() => setLoading(false));
    loadVerificationRequests();
  }, []);

  const approveVerification = (id: string) => {
    api.put(`/users/admin/verification-requests/${id}/approve`, {}).then(() => {
      setVerificationRequests(prev => prev.filter(r => r.id !== id));
      setUsers(prev => prev.map(u => (verificationRequests.find(r => r.id === id)?.user_id === u.id ? { ...u, is_verified: true } : u)));
    }).catch(() => {});
  };

  const rejectVerification = (id: string) => {
    api.put(`/users/admin/verification-requests/${id}/reject`, {}).then(() => {
      setVerificationRequests(prev => prev.filter(r => r.id !== id));
    }).catch(() => {});
  };

  // Refuser uniquement la photo de PROFIL (avatar_url, visible sur la carte à gauche du selfie/
  // pièce d'identité) — indépendant de la décision d'identité elle-même : la vérification peut
  // très bien être approuvée alors que la photo publique n'est pas présentable (voir
  // routes/users.ts#PUT /:id/reject-avatar, qui vide avatar_url et déclenche le popup dédié côté
  // client, jamais en même temps que le blocage "vérification requise").
  const rejectAvatar = (userId: string) => {
    if (!window.confirm("Refuser la photo de profil de cet utilisateur ? Elle sera retirée et il devra en choisir une autre.")) return;
    api.put(`/users/${userId}/reject-avatar`, {}).catch(() => {});
  };

  const toggleBan = (user: any) => {
    const verb = user.is_active ? "suspendre" : "réactiver";
    if (!window.confirm(`Confirmer : ${verb} le compte de ${user.name} ?`)) return;
    api.put(`/users/${user.id}/${user.is_active ? "ban" : "unban"}`, {}).then(data => {
      if (data?.error) { alert(data.error); return; }
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: !u.is_active } : u));
    }).catch(() => {});
  };

  // Connexion "en tant que" (voir OLTODO-backend/src/routes/adminStaff.ts) — jamais d'accès au mot
  // de passe (hashé, irréversible) : un code à usage unique de 60s, échangé côté oltodo-web dans
  // un nouvel onglet. Réservé au compte master.
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const loginAs = (user: any) => {
    if (impersonating) return;
    setImpersonating(user.id);
    api.post(`/admin/staff/${user.id}/impersonate`, {}).then(data => {
      if (data?.code) window.open(`https://oltodo.com/?impersonate=${data.code}`, "_blank");
      else if (data?.error) alert(data.error);
    }).catch(() => {}).finally(() => setImpersonating(null));
  };

  // Position réelle en direct (voir OLTODO-backend/src/routes/location.ts#GET /admin/:userId/live)
  // — contourne le partage entre utilisateurs (amis/discret/double_jeu), réservé au master.
  const [locationPreview, setLocationPreview] = useState<any | null>(null);
  const viewLocation = (user: any) => {
    setLocationPreview({ user, loading: true });
    api.get(`/location/admin/${user.id}/live`).then(data => {
      setLocationPreview({ user, ...data });
    }).catch(() => setLocationPreview({ user, enabled: false, error: true }));
  };

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const SHARING_MODE_LABEL: Record<string, string> = {
    discret: "Discret (aucun partage)", amis: "Visible par les amis",
    amis_choisis: "Visible par une liste choisie", double_jeu: "Double jeu",
  };

  return (
    <div style={{ padding: 32 }}>
      <PageHeader
        title="Utilisateurs"
        subtitle={`${users.length} inscrits au total`}
        action={<SearchInput value={search} onChange={setSearch} />}
      />

      {verificationRequests.length > 0 && (
        <Card noPadding style={{ marginBottom: 20 }}>
          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${colors.border}` }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>Demandes de vérification en attente</div>
            <div style={{ fontSize: 12.5, color: colors.textMuted, marginTop: 2 }}>{verificationRequests.length} demande{verificationRequests.length > 1 ? "s" : ""} à examiner</div>
          </div>
          {verificationRequests.map(r => (
            <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 20px", borderTop: `1px solid ${colors.border}` }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: colors.accent, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 13, fontWeight: 700, overflow: "hidden", flexShrink: 0 }}>
                {r.avatar_url ? <img src={r.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(r.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
                  {r.name}
                  {r.legal_name && r.legal_name !== r.name && <span style={{ fontWeight: 500, color: colors.textMuted }}> · nom réel : {r.legal_name}</span>}
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, backgroundColor: colors.border, color: colors.text }}>
                    {r.kind === "document" ? "Ajout de pièce justificative" : r.is_verified ? "Nouvelle demande" : "Vérification du compte"}
                  </span>
                  {r.document_type && <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, backgroundColor: colors.border, color: colors.text }}>{DOC_TYPE_LABELS[r.document_type] || r.document_type}</span>}
                  {r.kind !== "document" && !r.document_url && <span style={{ fontSize: 11, color: colors.textMuted }}>Selfie seul (pièce facultative non fournie)</span>}
                </div>
                <div style={{ fontSize: 12, color: colors.textMuted }}>{r.email}{r.city ? ` · ${r.city}` : ""} · demandé le {new Date(r.created_at).toLocaleDateString()}</div>
                {r.note && <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>« {r.note} »</div>}
              </div>
              {/* Selfie + pièce d'identité côte à côte — l'admin les compare visuellement avant
                  d'approuver. Les deux sont purgées côté serveur (Cloudinary + colonnes vidées)
                  dès que la demande est traitée, quel que soit le sens de la décision : elles ne
                  réapparaîtront donc plus jamais ici une fois approuvée/refusée. */}
              {r.selfie_url && (
                <a href={r.selfie_url} target="_blank" rel="noreferrer" title="Selfie" style={{ width: 44, height: 44, borderRadius: 8, overflow: "hidden", flexShrink: 0, display: "block" }}>
                  <img src={r.selfie_url} alt="selfie" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </a>
              )}
              {r.document_url && (
                <a href={r.document_url} target="_blank" rel="noreferrer" title={DOC_TYPE_LABELS[r.document_type] || "Pièce justificative"} style={{ width: 44, height: 44, borderRadius: 8, overflow: "hidden", flexShrink: 0, display: "block" }}>
                  <img src={r.document_url} alt="pièce justificative" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </a>
              )}
              <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap", maxWidth: 180 }}>
                {/* Décision sur la photo de PROFIL (avatar_url, à gauche) — indépendante de la
                    vérification d'identité elle-même, voir rejectAvatar. */}
                <Button variant="secondary" small onClick={() => rejectAvatar(r.user_id)}>Photo non conforme</Button>
                <Button variant="danger" small onClick={() => rejectVerification(r.id)}>Refuser</Button>
                <Button variant="primary" small onClick={() => approveVerification(r.id)}>Approuver</Button>
              </div>
            </div>
          ))}
        </Card>
      )}

      <TableCard headers={["Utilisateur", "Email", "Score", "Publications", "Amis", "Statut", "Inscrit", "Actions"]}>
        {loading ? (
          <tr><td colSpan={8}><LoadingRow /></td></tr>
        ) : filtered.map(user => (
          <tr key={user.id} style={trStyle}>
            <td style={td}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: colors.accent, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                  {initials(user.name)}
                </div>
                <div>
                  <div style={{ fontWeight: 600 }}>{user.name}</div>
                  {user.is_verified && <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, color: colors.accent, fontWeight: 600 }}><CheckIcon size={10} /> Vérifié</div>}
                </div>
              </div>
            </td>
            <td style={{ ...td, color: colors.textMuted }}>{user.email}</td>
            <td style={td}><span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: colors.success, fontWeight: 700 }}><StarIcon size={13} color={colors.success} /> {user.score}/10</span></td>
            <td style={{ ...td, color: colors.textMuted }}>{user.publications_count}</td>
            <td style={{ ...td, color: colors.textMuted }}>{user.friends_count}</td>
            <td style={td}><Badge tone={user.is_active ? "success" : "danger"} dot>{user.is_active ? "Actif" : "Suspendu"}</Badge></td>
            <td style={{ ...td, color: colors.textFaint, fontSize: 12.5 }}>{new Date(user.created_at).toLocaleDateString()}</td>
            <td style={td}>
              <div style={{ display: "flex", gap: 6 }}>
                {isMasterAdmin() && (
                  <Button small variant="ghost" disabled={impersonating === user.id} onClick={() => loginAs(user)} style={{ color: colors.text }}>
                    <LoginAsIcon size={13} /> {impersonating === user.id ? "..." : "Se connecter en tant que"}
                  </Button>
                )}
                {isMasterAdmin() && (
                  <Button small variant="ghost" onClick={() => viewLocation(user)} style={{ color: colors.text }}>
                    <PinIcon size={13} /> Localisation
                  </Button>
                )}
                <Button small variant={user.is_active ? "danger" : "secondary"} onClick={() => toggleBan(user)}>
                  {user.is_active ? "Suspendre" : "Réactiver"}
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </TableCard>

      <PreviewModal
        open={!!locationPreview}
        onClose={() => setLocationPreview(null)}
        title={locationPreview ? `Localisation, ${locationPreview.user.name}` : ""}
        subtitle={locationPreview?.enabled ? SHARING_MODE_LABEL[locationPreview.sharing_mode] || locationPreview.sharing_mode : undefined}
        body={
          locationPreview?.loading ? "Chargement…" :
          locationPreview?.error ? "Erreur lors de la récupération." :
          locationPreview?.enabled === false ? "Cette personne ne partage pas sa position en direct actuellement." :
          null
        }
        fields={locationPreview?.enabled ? [
          { label: "Coordonnées", value: `${Number(locationPreview.latitude).toFixed(5)}, ${Number(locationPreview.longitude).toFixed(5)}` },
          { label: "Dernière mise à jour", value: locationPreview.updated_at ? new Date(locationPreview.updated_at).toLocaleString("fr-FR") : "—" },
          { label: "Fraîcheur", value: locationPreview.is_stale ? "Ancienne (> 30 min)" : "En direct" },
          { label: "Carte", value: <a href={`https://www.google.com/maps?q=${locationPreview.latitude},${locationPreview.longitude}`} target="_blank" rel="noreferrer" style={{ color: colors.accent }}>Ouvrir dans Google Maps ↗</a> },
        ] : []}
      />
    </div>
  );
}
